import React, { useState, useEffect } from 'react';
import DataTable from '../DataTable';
import { getProcessedDocuments } from '../../utils/api';
import { useDocument } from '../../utils/DocumentContext';
import WindowProducts from '../Windows/WindowProducts';
import WindowPrint from '../Windows/WindowPrint';

export default function ProcessedDocumentsSection() {
  const [documentsData, setDocumentsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showProductsWindow, setShowProductsWindow] = useState(false);
  const [selectedDocumentForProducts, setSelectedDocumentForProducts] = useState(null);

  // Стан для вікна друку
  const [showPrintWindow, setShowPrintWindow] = useState(false);
  const [printDocument, setPrintDocument] = useState(null);
  const [printPositions, setPrintPositions] = useState([]);
  // Processed documents don't need status management - they're always "Оброблено"

  const { selectedDocument, selectDocument, clearDocument } = useDocument();

  useEffect(() => {
    fetchDocumentsData();
  }, []);
  const fetchDocumentsData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await getProcessedDocuments();      if (response.success && response.documents) {
        const processedDocuments = response.documents.map(doc => {
          // Count positions from the document
          const positionsCount = doc.Позиции ? doc.Позиции.length : 0;
          
          // Calculate totals from positions
          let totalPlan = 0;
          let totalFact = 0;
          
          if (doc.Позиции && Array.isArray(doc.Позиции)) {
            doc.Позиции.forEach(pos => {
              totalPlan += Number(pos.КоличествоПлан) || 0;
              totalFact += Number(pos.КоличествоФакт) || 0;
            });
          }        return {
            id: doc.ДокументНомер || doc.id,
            documentType: doc.ДокументВид || 'Не вказано',
            documentNumber: doc.ДокументНомер || 'Не вказано',
            documentDate: formatDate(doc.ДокументДата),
            processedDate: doc.ДатаОбработки ? formatDate(doc.ДатаОбработки) : 
                          (doc._fileInfo?.processedAt ? new Date(doc._fileInfo.processedAt * 1000).toLocaleString('uk-UA') : 'Не вказана'),
            positionsCount,
            totalPlan,
            totalFact,
            fileName: doc._fileInfo?.fileName || 'Невідомо',
            fileSize: doc._fileInfo?.fileSize || 0,
            status: 'Оброблено',
            rawDocument: doc
          };
        });

        setDocumentsData(processedDocuments);
      } else {
        setDocumentsData([]);
      }
    } catch (err) {
      console.error('Помилка завантаження оброблених документів:', err);
      setError('Помилка завантаження даних: ' + (err.message || 'Невідома помилка'));
    } finally {
      setLoading(false);
    }
  };
  // Processed documents are always "Обработан" - no status management needed

  const formatDate = (dateString) => {
    if (!dateString) return 'Не вказана';
    try {
      return new Date(dateString).toLocaleString('uk-UA');
    } catch {
      return 'Неправильна дата';
    }
  };

  const handleSelectDocument = (row) => {
    const adaptedDocument = {
      id: row.rawDocument.ДокументНомер,
      filename: row.rawDocument.ДокументНомер,
      created: row.rawDocument.ДокументДата,
      ДокументДата: row.rawDocument.ДокументДата,
      ДокументВид: row.rawDocument.ДокументВид,
      documentType: row.rawDocument.ДокументВид,
      size: JSON.stringify(row.rawDocument).length,
      data: row.rawDocument
    };

    const adaptedPositions = (row.rawDocument.Позиции || []).map(pos => ({
      id: pos.Код,
      article: pos.Код,
      name: pos.Наименование,
      barcode: pos.Штрихкод || pos.Код,
      plan: Number(pos.КоличествоПлан) || 0,
      fact: Number(pos.КоличествоФакт) || 0,
      reserve: Number(pos.КоличествоРезерв) || 0,
      status: pos.СтатусПозиции || '',
      category: pos.Категория || '',
      subcategory: pos.Подкатегория || '',
      brand: pos.ТорговаяМарка || '',
      warehouse: pos.Склад || ''
    }));

    selectDocument(adaptedDocument, adaptedPositions, true); // true = документ уже обработан
  };

  const handleToggleDocument = (row) => {
    const selected = isDocumentSelected(row.documentNumber);
    if (selected) {
      clearDocument();
    } else {
      handleSelectDocument(row);
    }
  };

  const isDocumentSelected = (documentNumber) =>
      selectedDocument && selectedDocument.filename === documentNumber;

  // Обробники для вікна товарів
  const handleRowClick = (row) => {
    setSelectedDocumentForProducts(row.rawDocument);
    setShowProductsWindow(true);
  };

  const handleCloseProductsWindow = () => {
    setShowProductsWindow(false);
    setSelectedDocumentForProducts(null);
  };

  // Обробники для вікна друку
  const handleOpenPrint = (printData = [], documentData = null, positions = []) => {
    console.log('handleOpenPrint викликано з параметрами:', { printData, documentData, positions });
    setPrintDocument(documentData);
    setPrintPositions(positions);
    setShowPrintWindow(true);
  };

  const handleClosePrintWindow = () => {
    setShowPrintWindow(false);
    setPrintDocument(null);
    setPrintPositions([]);
  };
  
  const columns = [
    // Status indicator - always "Оброблено" for processed documents
    {
      key: 'statusIndicator',
      title: 'Статус',
      width: '80px',
      textAlign: 'center',      render: () => (
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            margin: '0 auto',
            color: '#4caf50'
          }}
          title="Документ оброблено"
        >
          🟢
        </div>
      )
    },    { 
      key: 'documentType', 
      title: 'Вид документа', 
      width: '200px', 
      sortable: true, 
      filterable: true,
      render: (value) => <span style={{ fontWeight: 500, color: '#333' }}>{value}</span>
    },    {
      key: 'documentNumber',
      title: 'Номер документа',
      width: '150px',
      sortable: true,
      filterable: true,
      render: (value) => <span style={{ fontWeight: 600, color: '#1976d2' }}>{value}</span>
    },
    { 
      key: 'documentDate', 
      title: 'Дата документа', 
      width: '150px', 
      sortable: true,
      render: (value) => <span style={{ fontSize: '13px' }}>{value}</span>
    },
    { 
      key: 'processedDate', 
      title: 'Дата обработки', 
      width: '150px', 
      sortable: true,
      render: (value) => <span style={{ fontSize: '13px', color: '#4caf50' }}>{value}</span>
    },
    {
      key: 'positionsCount',
      title: 'Позиций',
      width: '80px',
      sortable: true,
      textAlign: 'center',
      render: (value) => <span style={{ fontWeight: 500, color: '#666' }}>{value}</span>
    },
    {
      key: 'totalPlan',
      title: 'План',
      width: '80px',
      sortable: true,
      textAlign: 'right',
      render: (value) => {
        const num = Number(value || 0);
        return <span style={{ fontWeight: 500 }}>{num}</span>;
      }
    },
    {
      key: 'totalFact',
      title: 'Факт',
      width: '80px',
      sortable: true,
      textAlign: 'right',
      render: (value) => {
        const num = Number(value || 0);
        return <span style={{ fontWeight: 500, color: value > 0 ? '#2e7d32' : '#666' }}>{num}</span>;
      }
    },
    {
      key: 'fileName',
      title: 'Файл',
      width: '200px',
      sortable: true,
      filterable: true,
      render: (value) => <span style={{ fontSize: '12px', color: '#666', fontFamily: 'monospace' }}>{value}</span>
    },
    {
      key: 'action',
      title: 'Дія',
      width: '120px',
      textAlign: 'center',
      render: (_, row) => {
        const selected = isDocumentSelected(row.documentNumber);
        return (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
              <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleDocument(row);
                  }}
                  style={{
                    padding: '4px 8px',
                    background: selected ? '#f44336' : '#4caf50',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: 500
                  }}
                  title={selected ? 'Скасувати вибір' : 'Обрати як поточний документ'}
              >
                {selected ? '✖' : '✓'}
              </button>
              <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenPrint([], row.rawDocument, row.rawDocument.Позиции || []);
                  }}                  style={{
                    padding: '4px 8px',
                    background: '#1976d2',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: 500
                  }}
                  title="Друк документа"
              >
                🖨️
              </button>
            </div>
        );
      }
    }
  ];

  if (loading) {
    return (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#666' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 16 }}>📄</div>
            <div>Завантаження документів...</div>
          </div>
        </div>
    );
  }

  if (error) {
    return (
        <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 20,
              color: '#d32f2f'
            }}
        >          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <div style={{ fontSize: 18, marginBottom: 10 }}>Помилка завантаження</div>
          <div style={{ fontSize: 14, textAlign: 'center', marginBottom: 20 }}>{error}</div>
          <button
              onClick={fetchDocumentsData}
              style={{ padding: '8px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
          >
            Повторити спробу
          </button>
        </div>
    );
  }

  const selectedRowIndex =
      selectedDocument ? documentsData.findIndex((d) => d.documentNumber === selectedDocument.filename) : null;

  return (
      <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            padding: window.innerWidth < 768 ? '8px 0' : '16px',
            boxSizing: 'border-box'
          }}
      >
        {/* Шапка секции — остаётся на месте */}
        <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: window.innerWidth < 768 ? '8px' : '16px',
              margin: window.innerWidth < 768 ? '0 8px 8px 8px' : '0 0 16px 0',
              padding: window.innerWidth < 768 ? '8px' : '12px',
              background: 'linear-gradient(135deg, #e8f5e8 0%, #4caf50 100%)',
              border: window.innerWidth < 768 ? 'none' : '1px solid #4caf50',
              borderRadius: window.innerWidth < 768 ? '0' : '8px',
              boxShadow: window.innerWidth < 768 ? 'none' : '0 2px 6px rgba(76, 175, 80, 0.2)',
              flexShrink: 0
            }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '32px', marginRight: '12px' }}>📋</span>
            <div>
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: '#2e7d32'
              }}>
                Processed Documents
              </h2>
              <div style={{
                fontSize: '14px',
                color: '#388e3c',
                fontWeight: '500',
                marginTop: '2px'
              }}>
                Оброблені документи • {documentsData.length} шт.
              </div>
            </div>
          </div>
          <button
              onClick={fetchDocumentsData}
              style={{
                padding: '4px 8px',
                background: 'rgba(255,255,255,0.8)',
                border: '1px solid #4caf50',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: '27px'
              }}
              title="Оновити список"
          >
              ⟲
          </button>
        </div>

        {/* Только таблица скроллится */}
        <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',           // ← важно: скролл внутри DataTable
              display: 'flex',
              flexDirection: 'column'
            }}
        >
          <DataTable
              data={documentsData}
              columns={columns}
              options={{
                showCheckboxes: false,
                showRowNumbers: false,
                striped: true,
                bordered: true,
                hoverable: true,
                sortable: true,
                filterable: true,
                emptyMessage: 'Немає оброблених документів',
                clickableRows: true,
                onRowClick: handleRowClick,
                selectedRow: selectedRowIndex
              }}
          />
        </div>

        {/* Окно товаров */}
        {showProductsWindow && selectedDocumentForProducts && (
            <WindowProducts
                isOpen={showProductsWindow}
                document={selectedDocumentForProducts}
                positions={selectedDocumentForProducts.Позиции || []}
                onClose={handleCloseProductsWindow}
                onOpenPrint={handleOpenPrint}
            />
        )}

        {/* Окно печати */}
        {showPrintWindow && (
            <WindowPrint
                onClose={handleClosePrintWindow}
                documentData={printDocument}
                positions={printPositions}
            />
        )}
      </div>
  );
}
